import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import BlogGate from "./BlogGate";
import {
  buildContentTrackingMetadata,
  getApiBaseUrl,
  storeContentSession,
} from "../lib/gatedContentTracking";

const STORAGE_KEY = "asha_blog_access_session";
const BLOG_SESSION_ID_KEY = "asha_blog_access_session_id";
const EXPIRY_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

const readStoredSession = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.sessionId || !parsed?.grantedAt) return null;
    if (Date.now() - parsed.grantedAt > EXPIRY_MS) return null;
    return parsed;
  } catch {
    return null;
  }
};

const hasAccessForContentType = (session, contentType) => {
  if (!session) return false;
  if (contentType === "video") return session.videoAttestation === true;
  return true;
};

const getTrackingContentType = (contentType) =>
  contentType === "video" ? "video" : "blog";

const GatedBlogRoute = ({ children, contentType = "article" }) => {
  const location = useLocation();
  const [hasAccess, setHasAccess] = useState(() =>
    hasAccessForContentType(readStoredSession(), contentType)
  );
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  useEffect(() => {
    if (!isHydrated || !hasAccess) return;

    const stored = readStoredSession();
    if (!stored?.sessionId) return;

    const contentMetadata = buildContentTrackingMetadata({
      contentType: getTrackingContentType(contentType),
    });
    storeContentSession(stored.sessionId, BLOG_SESSION_ID_KEY, contentMetadata);

    const dedupeKey = `asha_blog_view_recorded:${stored.sessionId}:${contentMetadata.contentRoute}`;
    try {
      if (sessionStorage.getItem(dedupeKey)) return;
      sessionStorage.setItem(dedupeKey, "true");
    } catch {
      // sessionStorage may be unavailable; still attempt the network record.
    }

    fetch(`${getApiBaseUrl()}/api/blog/view`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        sessionId: stored.sessionId,
        ...contentMetadata,
      }),
      keepalive: true,
    }).catch((err) => console.error("Failed to record blog view", err));
  }, [hasAccess, isHydrated, location.pathname]);

  const handleAccessGranted = (email, sessionId, extras = {}) => {
    try {
      const existing = readStoredSession() || {};
      const merged = {
        email,
        sessionId,
        grantedAt: Date.now(),
        videoAttestation:
          extras.videoAttestation === true || existing.videoAttestation === true,
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(merged));
      storeContentSession(
        sessionId,
        BLOG_SESSION_ID_KEY,
        buildContentTrackingMetadata({
          contentType: getTrackingContentType(contentType),
        })
      );
    } catch {
      // localStorage may be unavailable (private browsing, full quota).
      // Grant access for this session regardless.
    }
    setHasAccess(true);
  };

  // Avoid rendering article contents during the first paint so an SSR/cached
  // page can't briefly flash content before we check access.
  if (!isHydrated && !hasAccess) {
    return null;
  }

  if (hasAccess) {
    return children;
  }

  return (
    <BlogGate
      onAccessGranted={handleAccessGranted}
      contentType={contentType}
    />
  );
};

export default GatedBlogRoute;
